import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListQuoteTemplatesResult } from '@whinerp/client';
import { formatDate } from '@whinerp/contracts/format';
import { CapabilityUnavailable, ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Quotation templates, listed where a workspace is configured (F-158).
 *
 * The way into SC-43. Anybody who can quote can read the list - it is the
 * same list the new-quote picker offers - and only somebody who may change
 * templates is offered a new one.
 */
export function QuoteTemplatesPanel(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const { can } = useSession();
  const templating = useEntitlement('F-158');

  const templates = useQuery<ListQuoteTemplatesResult, ApiError>({
    queryKey: ['quote-templates', 'all'],
    queryFn: () => api.listQuoteTemplates({}),
    enabled: templating.enabled,
  });

  const heading = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <SectionLabel>{t('quoteTemplate.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('quoteTemplate.body')}</p>
      </div>
      {templating.enabled && can('quote.template.manage') ? (
        <Link
          to="/quote-templates/new"
          data-quote-template-new
          className="text-dense text-bronze-text hover:text-ink"
        >
          {t('quoteTemplate.new')}
        </Link>
      ) : null}
    </div>
  );

  if (templating.state === 'off' || templating.state === 'plan-locked') {
    return (
      <Panel data-region="quote-templates" id="quote-templates">
        <div className="flex flex-col gap-3">
          {heading}
          <CapabilityUnavailable
            kind={templating.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('quoteTemplate.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('quoteTemplate.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }

  return (
    <Panel data-region="quote-templates" id="quote-templates">
      <div className="flex flex-col gap-3">
        {heading}
        {templates.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : templates.isError ? (
          <ErrorState title={t('state.error')} body={errorText(templates.error)} onRetry={() => void templates.refetch()} />
        ) : templates.data.templates.length === 0 ? (
          <p data-quote-templates-none className="text-meta text-ink-meta">
            {t('quoteTemplate.none')}
          </p>
        ) : (
          <ul className="flex flex-col">
            {templates.data.templates.map((template) => (
              <li
                key={template.templateId}
                data-quote-template={template.templateId}
                className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
              >
                <Link
                  to={`/quote-templates/${template.templateId}`}
                  className="min-w-0 flex-1 truncate text-body text-ink hover:underline"
                >
                  {template.name}
                </Link>
                <span className="text-meta text-ink-meta">
                  {t('quoteTemplate.summary', {
                    values: {
                      currency: template.currency,
                      lines: String(template.lineCount),
                      version: String(template.version),
                      date: formatDate(template.updatedAt, format),
                    },
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
